import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Stack, IconButton, Typography, Button } from '@mui/material'
import { Add, Remove } from '@mui/icons-material'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import { formatPressure } from '../utils/string'
import { AuroraSetMaxPressureLimitActionInput } from '../types/AuroraAwhpActionInput'
import PinCodeDialog from './PinCodeDialog'
import useDialog from '../hooks/useDialog'
import { AuroraAwhpActionNack } from '../errors'
import AuroraControl from './AuroraControl'

export interface AuroraMaxPressureLimitControlProps {
  value: number | null
  loading?: boolean
  disabled?: boolean
  onSubmit: (input: AuroraSetMaxPressureLimitActionInput) => Promise<void>
}

export default function AuroraMaxPressureLimitControl({
  value,
  loading = false,
  disabled = false,
  onSubmit
}: AuroraMaxPressureLimitControlProps) {
  const { t } = useTranslation('aurora')

  const { open, openDialog, closeDialog } = useDialog()

  const [maxPressLimit, setMaxPressLimit] = useState<number | null>(null)

  function handleIncrement() {
    setMaxPressLimit((prev) => {
      const current = prev !== null ? prev : value
      if (current !== null) {
        return Math.ceil(current * 10) / 10 + 0.1
      }
      return null
    })
  }

  function handleDecrement() {
    setMaxPressLimit((prev) => {
      const current = prev !== null ? prev : value
      if (current !== null) {
        return Math.ceil(current * 10) / 10 - 0.1
      }
      return null
    })
  }

  async function handleSubmit(pin: number) {
    closeDialog()

    if (maxPressLimit === null) {
      return
    }

    try {
      await onSubmit({
        alarms: {
          pin,
          maxPressLimit
        }
      })

      setMaxPressLimit(null)
    } catch (error) {
      if (error instanceof AuroraAwhpActionNack) {
        switch (error.message) {
          case 'PIN_MISSING':
          case 'PIN_REJECTED':
            openDialog()
            break
        }
      }
    }
  }

  function handleReset() {
    setMaxPressLimit(null)
  }

  return (
    <>
      <AuroraControl
        direction="column"
        label={t('maxPressureLimit')}
        loading={loading}
        disabled={disabled}
        control={
          <Stack direction="column" justifyContent="center" alignItems="center" gap={2} pt={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={3}>
              <IconButton
                color={maxPressLimit !== null ? 'primary' : 'inherit'}
                disabled={loading || disabled}
                size="small"
                sx={{ border: '1px solid' }}
                onClick={handleDecrement}
              >
                <Remove />
              </IconButton>

              {maxPressLimit !== null ? (
                <Typography variant="h2" color={loading ? 'text.disabled' : 'primary'}>
                  {formatPressure(maxPressLimit, 'bar', 1)}
                </Typography>
              ) : (
                <Typography variant="h2" color="text.primary">
                  {formatPressure(value, 'bar', 1)}
                </Typography>
              )}

              <IconButton
                color={maxPressLimit !== null ? 'primary' : 'inherit'}
                disabled={loading || disabled}
                size="small"
                sx={{
                  border: '1px solid'
                }}
                onClick={handleIncrement}
              >
                <Add />
              </IconButton>
            </Stack>

            {maxPressLimit !== null && (
              <Stack direction="row" alignItems="center" gap={2}>
                <LoadingButton
                  variant="outlined"
                  color="primary"
                  size="small"
                  loading={loading}
                  disabled={disabled}
                  onClick={openDialog}
                >
                  {t('actions:confirm')}
                </LoadingButton>

                {!loading && (
                  <Button
                    variant="text"
                    color="inherit"
                    size="small"
                    disabled={disabled}
                    onClick={handleReset}
                  >
                    {t('actions:cancel')}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        }
      />

      <PinCodeDialog open={open} onClose={closeDialog} onSubmit={handleSubmit} />
    </>
  )
}
